import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import { cn } from "../../utils/cn";
import { BROCHURE_MODULES, type BrochureModule } from "../../data/brochureModules";
import type { BannerState } from "./SubscriptionBanner";

/**
 * Brochure overlay — the expanded surface that appears when a Free/Trial user
 * clicks the sticky banner. Covers the routed content area but stays underneath
 * the TopBar so users keep their bearings.
 *
 * Design rationale (impeccable: hierarchy + calm):
 *   - One clear headline. One clear sub. One clear primary CTA. Everything else
 *     is supporting evidence (the module grid).
 *   - Solid surface, not a scrim. The locked page peeking through a tinted
 *     overlay reads as "broken UI" — a clean paper-tone wipe reads as a
 *     dedicated screen the user navigated to on purpose.
 *   - Cards have soft accent tints, not saturated tile colours. A free user
 *     should leave with a feeling of "this app is calm and competent", not
 *     "this app is shouting at me about money".
 *   - Focus is trapped inside the overlay while it's open; ESC closes; the
 *     close button is large and unambiguous. Standard a11y for a takeover.
 */

const TOPBAR_HEIGHT_PX = 64; // matches h-16 on TopBar.tsx
const BANNER_HEIGHT_PX = 44; // matches h-11 on the sticky banner

interface BrochureOverlayProps {
  open: boolean;
  onClose: () => void;
  state: BannerState;
  onStartTrial: (e?: React.SyntheticEvent) => void;
  onRequestUpgrade: (e?: React.SyntheticEvent) => void;
}

const BrochureOverlay: React.FC<BrochureOverlayProps> = ({
  open,
  onClose,
  state,
  onStartTrial,
  onRequestUpgrade,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.dir() === "rtl";

  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // ESC closes. Focus trap keeps Tab inside the overlay while it's open.
  useEffect(() => {
    if (!open) return;

    function getFocusables(): HTMLElement[] {
      if (!containerRef.current) return [];
      const nodes = containerRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.from(nodes).filter((el) => !el.hidden && el.offsetParent !== null);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusables = getFocusables();
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    // Hand focus to the close button on open so screen readers announce
    // the takeover. A short rAF lets the overlay paint first.
    const id = window.requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.cancelAnimationFrame(id);
    };
  }, [open, onClose]);

  const handleExploreModule = useCallback(
    (mod: BrochureModule) => {
      // Phase 6 owns the per-module brochure pages. For now we route there
      // optimistically; if the page is missing the user falls through to 404.
      onClose();
      navigate(`/brochure/${mod.slug}`);
    },
    [navigate, onClose]
  );

  // ---------- Copy variants ----------

  const headline = t("Unlock everything in Mowazaf");
  let sub: string;
  if (state.variant === "free") {
    sub = t("You're on the free plan. Here's what a trial unlocks for your team.");
  } else if (state.variant === "trial-healthy" || state.variant === "trial-ending") {
    sub = t("Your trial ends in {{days}} days — here's everything you can still try.", {
      days: state.daysRemaining ?? 0,
    });
  } else if (state.variant === "trial-expired") {
    sub = t("Your trial ended. Start a new one to bring your full workspace back.");
  } else {
    sub = t("Your renewal is around the corner. Here's a refresher on what's included.");
  }

  // Primary / secondary CTAs in the hero. The "Continue using free" affordance
  // is just the close button in disguise — same handler, friendlier label.
  const showStartTrial =
    state.canStartTrial &&
    (state.variant === "free" || state.variant === "trial-expired");

  const showRequestUpgrade =
    state.variant === "trial-ending" || state.variant === "paid-expiring";

  return (
    <div
      id="subscription-brochure-overlay"
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="brochure-headline"
      aria-hidden={!open}
      className={cn(
        // Fixed under TopBar + banner. The total offset is 64 + 44 = 108px.
        // We use inline style so the magic number is centralized here and not
        // sprinkled into Tailwind arbitrary values throughout the file.
        "fixed inset-x-0 bottom-0 z-40 overflow-y-auto",
        // Soft "paper" surface — solid so the locked app behind doesn't bleed.
        "bg-gradient-to-b from-white via-slate-50 to-slate-100",
        // Enter/exit transitions. We keep the overlay mounted but hidden so the
        // ESC handler can be attached unconditionally.
        "transition-all duration-300 ease-out",
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      )}
      style={{ top: TOPBAR_HEIGHT_PX + BANNER_HEIGHT_PX }}
    >
      <div className="mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-14">
        {/* Close button — large hit target, sits in the start corner so it doesn't
            collide with anything in the hero. RTL-aware via end-/start- logical props. */}
        <div className="flex justify-end">
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t("Close")}
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full",
              "bg-white/80 text-slate-600 shadow-sm ring-1 ring-slate-200",
              "hover:bg-white hover:text-slate-900 hover:shadow-md",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
              "transition-all"
            )}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Hero — calm, one column, generous whitespace. The accent gradient on
            the headline gives the page a little personality without shouting. */}
        <div className="mt-2 max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
            {t("Mowazaf for everyone")}
          </p>
          <h1
            id="brochure-headline"
            className="mt-3 text-3xl font-semibold leading-tight text-slate-900 md:text-4xl"
          >
            <span className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 bg-clip-text text-transparent">
              {headline}
            </span>
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{sub}</p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {showStartTrial && (
              <button
                type="button"
                onClick={onStartTrial}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg px-5 py-2.5",
                  "bg-indigo-600 text-sm font-semibold text-white shadow-sm",
                  "hover:bg-indigo-700 hover:shadow",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                  "transition-all"
                )}
              >
                <Icon name="Rocket" size={16} />
                {t("Start free trial")}
                <Icon name="ArrowRight" size={14} className={isRtl ? "rotate-180" : ""} />
              </button>
            )}
            {showRequestUpgrade && (
              <button
                type="button"
                onClick={onRequestUpgrade}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg px-5 py-2.5",
                  "bg-slate-900 text-sm font-semibold text-white shadow-sm",
                  "hover:bg-slate-800",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-2",
                  "transition-all"
                )}
              >
                <Icon name="MailPlus" size={16} />
                {t("Request upgrade")}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg px-4 py-2.5",
                "text-sm font-medium text-slate-700",
                "hover:bg-slate-100 hover:text-slate-900",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                "transition-all"
              )}
            >
              {state.variant === "free"
                ? t("Continue using free")
                : t("Back to my workspace")}
            </button>
          </div>
        </div>

        {/* Module grid — what you get. 8 cards in 4 cols on desktop, 2 on tablet,
            1 on mobile. Each card is a real <button> so keyboard nav is free. */}
        <section className="mt-14" aria-labelledby="brochure-modules-heading">
          <div className="flex items-baseline justify-between">
            <h2
              id="brochure-modules-heading"
              className="text-sm font-semibold uppercase tracking-wider text-slate-500"
            >
              {t("Every module, included")}
            </h2>
            <span className="text-xs text-slate-400">
              {t("{{count}} modules", { count: BROCHURE_MODULES.length })}
            </span>
          </div>

          <ul
            className={cn(
              "mt-5 grid gap-4",
              "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            )}
          >
            {BROCHURE_MODULES.map((mod) => (
              <li key={mod.slug}>
                <button
                  type="button"
                  onClick={() => handleExploreModule(mod)}
                  className={cn(
                    "group flex h-full w-full flex-col items-start gap-3 rounded-xl",
                    "border border-slate-200 bg-white p-5 text-start",
                    "shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
                    "transition-all duration-200",
                    "hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300",
                    "ring-2 ring-transparent",
                    mod.accent.ring,
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex h-10 w-10 items-center justify-center rounded-lg",
                      mod.accent.surface
                    )}
                  >
                    <Icon name={mod.iconName} size={20} className={mod.accent.iconText} />
                  </span>
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-slate-900">
                      {t(mod.nameKey)}
                    </p>
                    <p className="text-sm leading-relaxed text-slate-500">
                      {t(mod.pitchKey)}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "mt-auto inline-flex items-center gap-1 text-xs font-semibold",
                      "text-slate-500 group-hover:text-slate-900 transition-colors"
                    )}
                  >
                    {t("Explore")}
                    <Icon
                      name="ArrowRight"
                      size={12}
                      className={cn(
                        "transition-transform group-hover:translate-x-0.5",
                        isRtl ? "rotate-180 group-hover:-translate-x-0.5 group-hover:translate-x-0" : ""
                      )}
                    />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Footer band — quiet, just two muted links so the page doesn't end abruptly. */}
        <footer className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6 text-xs text-slate-500">
          <p>{t("Questions about what's included? We're happy to walk you through it.")}</p>
          <div className="flex items-center gap-4">
            <a
              href="https://wa.me/971547888992"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-700 hover:text-indigo-600"
            >
              {t("Chat on WhatsApp")}
            </a>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <a
              href="https://mowazaf.com/contact-us/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-700 hover:text-indigo-600"
            >
              {t("Contact sales")}
            </a>
            <span aria-hidden="true" className="text-slate-300">·</span>
            <a
              href="https://mowazaf.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-700 hover:text-indigo-600"
            >
              {t("See pricing")}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default BrochureOverlay;
